import { issueReasons } from '@vromo/models';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import format from 'date-fns/format';
import { capitalize, range, sortBy } from 'lodash';
import { useLocalObservable } from 'mobx-react-lite';
import { createTransformer } from 'mobx-utils';
import React, { CSSProperties, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import GridHeaderCell from '../components/GridHeaderCell';
import { CommentLink, GridCellDisplay } from '../components/styledComponents';
import { JobHistoryModal, JobHistoryResponse } from './JobHistoryHelper';
import getReadableFromDateRange from './getReadableFromDateRange';

const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm';

type Column = {
    label: string;
    width?: number;
    getValue: (job: JobHistoryResponse) => string | number;
    render?: (job: JobHistoryResponse) => ReactElement;
    sortable?: string;
    align?: 'left' | 'right' | 'center';
};

interface CellRendererProps {
    columnIndex: number;
    key: string;
    rowIndex: number;
    style: CSSProperties;
}

interface Props {
    jobHistoryItems: JobHistoryResponse[];
    setSelectedJob: (jobData: JobHistoryResponse) => void;
    setActiveModal: (modalName: JobHistoryModal) => void;
}

const useTableStore = ({ jobHistoryItems, setSelectedJob, setActiveModal }: Props) => {
    const { t } = useTranslation();

    const store = useLocalObservable(() => ({
        sortColumn: -1,
        sortDirection: -1,
        get rowCount(): number {
            return store.sortedJobs.length + 1;
        },
        get sortedJobs(): JobHistoryResponse[] {
            if (this.sortColumn === -1) return jobHistoryItems;

            const sortColumn = this.columns[this.sortColumn];
            if (!sortColumn) throw new Error(`Invalid sort column index ${this.sortColumn}`);
            const sortedJobs = sortBy(jobHistoryItems, (it) => sortColumn.getValue(it));
            if (this.sortDirection === -1) sortedJobs.reverse();

            return sortedJobs;
        },

        get maxTasks() {
            return Math.max(
                ...(jobHistoryItems.map((it: JobHistoryResponse) => it.tasks.length) || []),
                0
            );
        },

        get maxIssues() {
            return Math.max(
                ...(jobHistoryItems.map((it: JobHistoryResponse) => it.issues.length) || []),
                0
            );
        },

        get maxPhotos() {
            return Math.max(
                ...(jobHistoryItems.map(
                    (it: JobHistoryResponse) =>
                        it.uploads.filter((u) => u.attachmentType === 'PHOTO').length
                ) || []),
                0
            );
        },

        get attrSet() {
            const result = new Set<string>();

            jobHistoryItems.forEach((item) => {
                item.attr &&
                    item.attr.forEach((attr) => {
                        if (attr.key && attr.key !== 'deliverBy') {
                            result.add(attr.key);
                        }
                    });
            });

            return result;
        },

        get columns() {
            return [
                {
                    label: t('job.jobName'),
                    width: 300,
                    getValue: createTransformer((job: JobHistoryResponse) => job.name)
                },
                {
                    label: t('jobHistory.status'),
                    getValue: createTransformer((job: JobHistoryResponse) => capitalize(job.status))
                },
                {
                    label: t('jobHistory.driver'),
                    getValue: createTransformer(
                        (job: JobHistoryResponse) =>
                            job.driver.contact.name || job.driver.contact.email || null
                    )
                },
                {
                    label: t('jobHistory.contactName'),
                    getValue: createTransformer(
                        (job: JobHistoryResponse) => job.customerContact?.name
                    )
                },
                {
                    label: t('jobHistory.contactEmail'),
                    width: 200,
                    getValue: createTransformer(
                        (job: JobHistoryResponse) => job.customerContact?.email
                    )
                },
                {
                    label: t('jobHistory.contactPhone'),
                    getValue: createTransformer((job: JobHistoryResponse) =>
                        String(job.customerContact?.phone || '')
                    )
                },
                {
                    label: t('jobHistory.created'),
                    width: 150,
                    getValue: createTransformer((job: JobHistoryResponse) =>
                        job?.created ? format(job?.created, DATE_TIME_FORMAT) : ''
                    )
                },
                {
                    label: t('jobHistory.started'),
                    width: 150,
                    getValue: createTransformer((job: JobHistoryResponse) =>
                        job?.started ? format(job?.started, DATE_TIME_FORMAT) : ''
                    )
                },
                ...range(0, this.maxTasks).reduce<Column[]>((taskCols, ix) => {
                    taskCols.push({
                        label: t('jobHistory.taskDeadline', { taskNumber: ix + 1 }),
                        width: 150,
                        getValue: createTransformer((jobHistoryItem: JobHistoryResponse) =>
                            jobHistoryItem.tasks.length > ix && jobHistoryItem.tasks[ix]?.deadline
                                ? format(jobHistoryItem.tasks[ix]!.deadline!, DATE_TIME_FORMAT)
                                : ''
                        )
                    });
                    taskCols.push({
                        label: t('jobHistory.taskCompleted', { taskNumber: ix + 1 }),
                        width: 150,
                        getValue: createTransformer((job: JobHistoryResponse) =>
                            job.tasks.length > ix && job.tasks[ix]?.completed
                                ? format(job.tasks[ix]!.completed!, DATE_TIME_FORMAT)
                                : ''
                        )
                    });
                    taskCols.push({
                        label: t('jobHistory.taskLateness', { taskNumber: ix + 1 }),
                        width: 150,
                        align: 'right',
                        getValue: createTransformer(
                            (job: JobHistoryResponse) =>
                                (!!job.tasks[ix]?.completed &&
                                    !!job.tasks[ix]?.deadline &&
                                    job.tasks[ix]!.completed - job.tasks[ix]!.deadline!) ||
                                0
                        ),
                        render: createTransformer((job: JobHistoryResponse) => {
                            const result = getReadableFromDateRange(
                                job.tasks[ix]?.deadline,
                                job.tasks[ix]?.completed
                            );
                            return <>{result}</>;
                        })
                    });

                    return taskCols;
                }, []),
                ...range(0, this.maxIssues).reduce<Column[]>((issueCols, ix) => {
                    issueCols.push({
                        label: t('jobHistory.issue', { issueNumber: ix + 1 }),
                        getValue: createTransformer((job: JobHistoryResponse) =>
                            job.issues.length > ix && job.issues[ix]?.type
                                ? String(issueReasons[job.issues[ix]?.type])
                                : ''
                        )
                    });
                    issueCols.push({
                        label: t('jobHistory.issueCreated', { issueNumber: ix + 1 }),
                        getValue: createTransformer((job: JobHistoryResponse) =>
                            job.issues.length > ix && job.issues[ix]!.created
                                ? format(job.issues[ix]!.created!, DATE_TIME_FORMAT)
                                : ''
                        )
                    });

                    return issueCols;
                }, []),
                {
                    label: t('jobHistory.completed'),
                    width: 150,
                    getValue: createTransformer((job: JobHistoryResponse) => {
                        const completedTime = job.completed;
                        return completedTime ? format(completedTime, DATE_TIME_FORMAT) : '';
                    })
                },
                {
                    label: t('jobHistory.rating'),
                    getValue: createTransformer((job: JobHistoryResponse) => job.rating)
                },
                {
                    label: t('jobHistory.photos'),
                    getValue: (job: JobHistoryResponse) =>
                        job.uploads.filter((it) => it.attachmentType === 'PHOTO').length,
                    render: (job: JobHistoryResponse) => (
                        <CommentLink
                            onClick={() => {
                                setSelectedJob(job);
                                setActiveModal('PHOTO');
                            }}>
                            {`${job.uploads.filter((it) => it.attachmentType === 'PHOTO').length} `}
                            <FontAwesome icon="images" weight="regular" />
                        </CommentLink>
                    )
                },
                {
                    label: t('jobHistory.signatures'),
                    width: 200,
                    getValue: () => 'signatures',
                    render: (job: JobHistoryResponse) => {
                        return (
                            <div>
                                {(job.uploads || { items: [] })
                                    .filter((it) => it.attachmentType === 'SIGNATURE')
                                    .map((it, ix) => (
                                        <span key={it.id}>
                                            <a href={it.url!} target="_blank">
                                                {ix + 1} <i className="fas fa-external-link-alt" />
                                            </a>{' '}
                                        </span>
                                    ))}
                            </div>
                        );
                    }
                },
                ...range(0, this.maxTasks).map((ix) => ({
                    label: t('jobHistory.address', { addressNumber: ix + 1 }),
                    width: 400,
                    getValue: createTransformer((job: JobHistoryResponse) =>
                        job.tasks.length > ix ? job.tasks[ix]?.geoZone.address : ''
                    )
                })),
                ...range(0, this.attrSet.size).map((ix) => {
                    const attr = [...this.attrSet][ix];

                    return {
                        label: attr || '',
                        width: 200,
                        getValue: createTransformer((job: JobHistoryResponse) => {
                            const jobAttr = job.attr && job.attr.find((it) => it.key === attr);
                            return jobAttr ? jobAttr.value : '';
                        })
                    };
                }),
                {
                    label: t('jobHistory.chatHistory'),
                    getValue: (job: JobHistoryResponse) => job.chat.length,
                    render: (job: JobHistoryResponse) => {
                        const onClick = () => {
                            setSelectedJob(job);
                            setActiveModal('CHAT');
                        };
                        return (
                            <CommentLink onClick={onClick}>
                                {job.chat.length} <FontAwesome icon="comment-alt" />
                            </CommentLink>
                        );
                    }
                },
                {
                    label: t('jobHistory.auditHistory'),
                    getValue: (job: JobHistoryResponse) => job.dispatches.length,
                    render: (job: JobHistoryResponse) => {
                        const onClick = () => {
                            setSelectedJob(job);
                            setActiveModal('AUDIT');
                        };
                        return (
                            <CommentLink onClick={onClick}>
                                {job.dispatches.length}{' '}
                                <FontAwesome icon="clipboard-list-check" weight="regular" />
                            </CommentLink>
                        );
                    }
                }
            ] as Column[];
        },

        setSortColumn(index: number) {
            const column = this.columns[index];

            if (!column || column.sortable === 'false') return;

            if (this.sortColumn === index) return (this.sortDirection = -this.sortDirection);

            this.sortColumn = index;
            this.sortDirection = 1;
        },

        get columnCount() {
            return 1 + this.columns.length;
        }
    }));

    const cellRenderer = ({ columnIndex, key, rowIndex, style }: CellRendererProps) => {
        const isHeader = rowIndex === 0;

        const column = store.columns[columnIndex];

        if (!column) return <span style={style} />;

        if (isHeader) {
            const { setSortColumn, sortColumn, sortDirection } = store;
            return (
                <GridHeaderCell
                    {...{ style, key, columnIndex, setSortColumn, sortColumn, sortDirection }}>
                    {column.label}
                </GridHeaderCell>
            );
        }

        const job = store.sortedJobs[rowIndex - 1];
        const value = column.getValue(job!);
        const rendered = column.render ? column.render(job!) : value;

        const cellStyle = {
            ...style,
            ...(column.align && { textAlign: column.align })
        };

        return (
            <GridCellDisplay style={cellStyle} title={`${value || ''}`}>
                {rendered}
            </GridCellDisplay>
        );
    };

    const getColumnWidth = ({ index }: { index: number }) => {
        const column = store.columns[index];
        return (column && column.width) || 120;
    };

    return { getColumnWidth, cellRenderer, store };
};

export default useTableStore;
